/* eslint-disable react-refresh/only-export-components -- Provider and optional consumer hooks share the same quote-only context contract. */
import { QuoteChatHostProvider } from "./QuoteChatWorkspaceHost";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import type { QuoteReadIdentity } from "@/api/quoteReadIdentity";
import {
  ensureQuoteChatScope,
  quoteChatCredentialSnapshot,
  quoteChatDemandCount,
  quoteChatScopeCurrent,
  quoteChatVerificationStatus,
  retainQuoteChatScope,
  retryQuoteChatVerification,
  subscribeQuoteChatDemand,
  subscribeQuoteChatVerification,
  type QuoteChatScope,
} from "@/api/quoteChatTransport";
import { quoteUuid } from "@/api/quoteChatArtifacts";
import { createQuoteChatRegistry } from "./quoteChatRegistry";
import type {
  QuoteChatWorksheetLookup,
  QuoteChatWorksheetAccess,
  RegisterQuoteChatAccess,
} from "./quoteChatWorkspaceAccess";
export { useQuoteChatLayout } from "./useQuoteChatLayout";

export type QuoteChatWorkspaceView = {
  quoteId: string;
  sessionId: string;
  pane: "details" | "ask_ai";
  requestedOpen: boolean;
  origin: "direct" | "journey";
  requestId: number;
};
export type QuoteChatWorkspaceApi = {
  identity: QuoteReadIdentity | null;
  status: "checking" | "ready" | "blocked" | "error";
  view: QuoteChatWorkspaceView | null;
  registerAccess: RegisterQuoteChatAccess;
  lookupAccess: (quoteId: string) => QuoteChatWorksheetLookup;
  openAskAi: (quoteId: string, options?: { sessionId?: string }) => boolean;
  closeAskAi: () => void;
  retryIdentity: () => void;
};
const Context = createContext<QuoteChatWorkspaceApi | null>(null);
const ScopeFactory = createContext<
  ((sessionId: string) => QuoteChatScope | null) | null
>(null);
const identityKey = (value: QuoteReadIdentity | null) =>
  value ? JSON.stringify([value.tenantId, value.userId, value.role]) : "";
const emptySubscribe = () => () => {};
function subscribeCredential(notify: () => void) {
  const storage = (event: StorageEvent) => {
    if (
      (event.storageArea === null || event.storageArea === localStorage) &&
      (event.key === null ||
        [
          "cnc.access_token",
          "cnc.refresh_token",
          "cnc.tenant_id",
          "cnc.user_id",
          "cnc.role",
        ].includes(event.key))
    )
      notify();
  };
  window.addEventListener("storage", storage);
  window.addEventListener("focus", notify);
  document.addEventListener("visibilitychange", notify);
  const timer = window.setInterval(notify, 100);
  return () => {
    window.removeEventListener("storage", storage);
    window.removeEventListener("focus", notify);
    document.removeEventListener("visibilitychange", notify);
    window.clearInterval(timer);
  };
}

/** The one worksheet registry lives at the Journey common ancestor. Idle mount
 * adds no verification requests/credential observer to ordinary/public pages.
 */
export function QuoteChatWorkspaceProvider({
  identity,
  children,
}: {
  identity: QuoteReadIdentity | null;
  children: ReactNode;
}) {
  const [hostAmbiguous, setHostAmbiguous] = useState(false);
  const actor = identityKey(identity),
    [registryRevision, bump] = useReducer((n: number) => n + 1, 0);
  const latest = useRef({ identity, ready: false, live: true });
  latest.current.identity = identity;
  const holder = useRef<{
    actor: string;
    registry: ReturnType<typeof createQuoteChatRegistry>;
  } | null>(null);
  if (holder.current?.actor !== actor)
    holder.current = {
      actor,
      registry: createQuoteChatRegistry(
        () => latest.current.identity,
        () => latest.current.ready,
        bump,
      ),
    };
  const registry = holder.current.registry;
  const [storedView, setStoredView] = useState<{
    actor: string;
    view: QuoteChatWorkspaceView;
    access: QuoteChatWorksheetAccess;
  } | null>(null);
  const sequence = useRef(0),
    currentView = useRef(storedView);
  currentView.current = storedView;
  const view = useMemo(() => {
    // Entries mutate behind the stable registry object; its notification
    // revision must invalidate this captured access reference.
    void registryRevision;
    return storedView?.actor === actor
      ? {
          ...storedView.view,
          requestedOpen:
            storedView.view.requestedOpen &&
            registry.registered(storedView.access),
        }
      : null;
  }, [storedView, actor, registry, registryRevision]);
  const demands = useSyncExternalStore(
    subscribeQuoteChatDemand,
    () => quoteChatDemandCount(identity),
    () => 0,
  );
  const active =
    !!identity && (registry.size() > 0 || demands > 0 || !!view?.requestedOpen);
  const credential = useSyncExternalStore(
    active ? subscribeCredential : emptySubscribe,
    active ? quoteChatCredentialSnapshot : () => "",
    () => "",
  );
  const factory = useCallback((sessionId: string): QuoteChatScope | null => {
    const captured = latest.current.identity;
    if (!captured) return null;
    const capturedKey = identityKey(captured);
    return {
      identity: captured,
      sessionId,
      owner: {},
      owns: () =>
        latest.current.live &&
        identityKey(latest.current.identity) === capturedKey,
    };
  }, []);
  const verificationScope = useMemo(
    () => (actor ? factory("quote-workspace-verification") : null),
    [factory, actor],
  );
  const status = useSyncExternalStore<QuoteChatWorkspaceApi["status"]>(
    subscribeQuoteChatVerification,
    () =>
      active && verificationScope
        ? quoteChatVerificationStatus(verificationScope)
        : "blocked",
    () => "blocked",
  );
  const ready =
    active &&
    !hostAmbiguous &&
    !!verificationScope &&
    quoteChatScopeCurrent(verificationScope);
  latest.current.ready = ready;
  useLayoutEffect(() => {
    const current = latest.current;
    current.live = true;
    return () => {
      current.live = false;
    };
  }, []);
  useEffect(() => {
    if (active && verificationScope)
      void ensureQuoteChatScope(verificationScope).catch(() => undefined);
  }, [active, verificationScope, credential]);
  const retryIdentity = useCallback(() => {
    retryQuoteChatVerification();
    if (active && verificationScope)
      void ensureQuoteChatScope(verificationScope).catch(() => undefined);
  }, [active, verificationScope]);
  const requestId = view?.requestId;
  const closeAskAi = useCallback(() => {
    if (
      currentView.current?.actor !== actor ||
      currentView.current.view.requestId !== requestId
    )
      return;
    setStoredView((value) =>
      value?.actor === actor && value.view.requestId === requestId
        ? { ...value, view: { ...value.view, requestedOpen: false } }
        : value,
    );
  }, [actor, requestId]);
  const openAskAi = useCallback(
    (quoteId: string, options?: { sessionId?: string }) => {
      if (
        identityKey(latest.current.identity) !== actor ||
        !latest.current.ready ||
        !quoteUuid(quoteId)
      )
        return false;
      const found = registry.lookup(quoteId);
      if (found.kind !== "active") return false;
      const sessionId =
        options?.sessionId ??
        `quote-side:${found.access.identity.userId}:${quoteId}`;
      if (!sessionId || sessionId.length > 128) return false;
      const origin =
        window.location.pathname === "/app/agent" ? "journey" : "direct";
      setStoredView({
        actor,
        access: found.access,
        view: {
          quoteId,
          sessionId,
          pane: "ask_ai",
          requestedOpen: true,
          origin,
          requestId: ++sequence.current,
        },
      });
      return true;
    },
    [actor, registry],
  );
  const api = useMemo<QuoteChatWorkspaceApi>(
    () => ({
      identity,
      status: hostAmbiguous ? "blocked" : ready ? "ready" : status,
      view,
      registerAccess: registry.register,
      lookupAccess: registry.lookup,
      openAskAi,
      closeAskAi,
      retryIdentity,
    }),
    [
      identity,
      hostAmbiguous,
      ready,
      status,
      view,
      registry,
      openAskAi,
      closeAskAi,
      retryIdentity,
    ],
  );
  return (
    <Context.Provider value={api}>
      <ScopeFactory.Provider value={factory}>
        <QuoteChatHostProvider workspace={api} onAmbiguous={setHostAmbiguous}>
          {children}
        </QuoteChatHostProvider>
      </ScopeFactory.Provider>
    </Context.Provider>
  );
}
export const useQuoteChatWorkspace = () => useContext(Context);
/** Scope lifetime belongs to the actor, not the mounted drawer. Its retained
 * run can continue after this hook unmounts; actual actor/credential fences remain.
 */
export function useQuoteChatScope(
  sessionId: string | null,
  demand = false,
): QuoteChatScope | null {
  const factory = useContext(ScopeFactory),
    workspace = useContext(Context),
    actor = identityKey(workspace?.identity ?? null);
  const scope = useMemo(
    () => (actor && sessionId && factory ? factory(sessionId) : null),
    [factory, sessionId, actor],
  );
  useEffect(
    () => (scope && demand ? retainQuoteChatScope(scope) : undefined),
    [scope, demand],
  );
  return scope;
}
